<template>
  <vetster-input-group
    :id="getId"
    :label="label"
    :feedback="feedback || null"
    :shake="shake"
    :error="error"
    :optional="optional"
    :is-private="isPrivate"
  >
    <div
      :class="{
        'mt-1': label,
        'rounded-md shadow-sm': !borderless,
      }"
      class="relative"
    >
      <select
        :id="getId"
        ref="select"
        v-model="currentValue"
        :aria-label="ariaLabel || label"
        :autofocus="autofocus"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :multiple="multiple"
        :class="currentClass"
        class="cursor-pointer block w-full border-neutral-300 focus:border-ocean-300 hover:ring focus:ring hover:ring-neutral-100 hover:shadow-md focus:ring-ocean-200/50"
        @blur="onBlur"
        @focus="onFocus"
      >
        <template v-for="(option, index) in normalizedOptionsWithPlaceholder">
          <optgroup
            v-if="option.children"
            :key="`${option.value}-optgroup-${index}`"
            :value="option.value"
            :label="option.text"
          >
            <option
              v-for="(childOption, index2) in option.children"
              :key="`${childOption.value}-${index}-${index2}`"
              :value="childOption.value"
              v-text="childOption.text"
            />
          </optgroup>
          <option
            v-else
            :key="`${option.value}-${index}`"
            :value="option.value"
            v-text="option.text"
          />
        </template>
      </select>
      <div
        v-if="error"
        class="absolute inset-y-0 right-5 pr-3 flex items-center pointer-events-none"
      >
        <fa
          :icon="['fal', 'exclamation-circle']"
          class="fa-fe text-lg text-red-500"
        />
      </div>
    </div>
  </vetster-input-group>
</template>

<script>
import get from 'lodash/get';

import VetsterInputGroup from '@/components/VetsterInputGroup';
import { uuid } from '@/helpers/uuid';
import CommonAttributes from '@/mixins/CommonAttributes';

export default {
  name: 'VetsterSelect',

  components: { VetsterInputGroup },

  mixins: [CommonAttributes],

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    bgTransparent: {
      type: Boolean,
      default: false,
    },
    borderless: {
      type: Boolean,
      default: false,
    },
    valueAttribute: {
      type: String,
      default: undefined,
    },
    textAttribute: {
      type: String,
      default: undefined,
    },
    options: {
      type: [Array, Object],
      default: () => [],
    },
    modelValue: {
      type: [String, Number, Array, Object],
      default: null,
    },
    reset: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    isPrivate: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: 'base',
    },
    shake: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },

  emits: [
    'blur',
    'change',
    'focus',
    'update:modelValue',
  ],

  data() {
    return {
      currentValue: this.modelValue,
    };
  },

  computed: {
    currentClass() {
      let theClass = '';
      if (this.disabled) {
        theClass += 'opacity-50 ';
      }
      if (this.size === 'xl') {
        theClass += 'sm:text-xl text-lg py-3.5';
      } else {
        theClass += 'text-sm';
      }
      if (this.error) {
        theClass += `${theClass}border-red-300 text-red-800 placeholder-red-300 focus:border-red-300 focus:ring-red`;
      }
      if (this.bgTransparent) {
        theClass += ' bg-transparent';
      }

      return theClass;
    },
    getId() {
      return this.id || uuid();
    },
    normalizedOptionsWithPlaceholder() {
      if (this.placeholder === undefined || !this.placeholder) {
        return this.normalizedOptions;
      }

      const { normalizedOptions } = this;
      normalizedOptions.unshift({
        value: null,
        text: this.placeholder,
      });

      return normalizedOptions;
    },
    normalizedOptions() {
      if (Array.isArray(this.options)) {
        return this.options.map((option) => this.normalizeOption(option));
      }

      return Object.keys(this.options).map((key) => ({
        value: key,
        text: this.options[key],
      }));
    },
  },

  watch: {
    currentValue(currentValue) {
      this.$emit('update:modelValue', currentValue);
      this.$emit('change', currentValue);
    },
    modelValue(value) {
      this.currentValue = value;
    },
  },

  methods: {
    normalizeOption(option) {
      if (['string', 'number', 'boolean'].includes(typeof option)) {
        return {
          value: option,
          text: option,
        };
      }

      if (option.children && Array.isArray(option.children)) {
        return {
          value: this.guessOptionValue(option),
          text: this.guessOptionText(option),
          children: option.children.map((childOption) => this.normalizeOption(childOption)),
        };
      }

      return {
        value: this.guessOptionValue(option),
        text: this.guessOptionText(option),
      };
    },
    guessOptionValue(option) {
      if (this.valueAttribute) {
        return get(option, this.valueAttribute);
      }

      return get(option, 'value', get(option, 'id', get(option, 'text')));
    },
    guessOptionText(option) {
      if (this.textAttribute) {
        return get(option, this.textAttribute);
      }

      return get(option, 'text', get(option, 'label'));
    },
    onBlur(e) {
      this.$emit('blur', e);
    },
    onFocus(e) {
      this.$emit('focus', e);
    },
    blur() {
      this.$refs.select.blur();
    },
    focus() {
      this.$refs.select.focus();
    },
  },
};
</script>
